import LoadingSpinner from "@/components/LoadingSpinner";
import useApi from "@/hooks/useApi";
import React, { useCallback } from "react";
import { useEffect } from "react";
import { useState } from "react";
import AddFieldModal from "./AddFieldModal";

export interface FieldOption {
  label: string;
  value: string;
  id: string;
}

export interface FieldDefinition {
  type: "text" | "select" | "checkbox" | "textarea";
  options: Array<FieldOption>;
  label: string;
}

interface ContactFieldDef {
  id: number;
  name: string;
  label: string;
  type: string;
  options?: string;
  sortOrder: number;
  isRequired: boolean;
}

export default function CustomFields() {
  const api = useApi();
  const [showAddFieldModal, setShowAddFieldModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [fields, setFields] = useState<ContactFieldDef[]>([]);
  const [maxFields, setMaxFields] = useState(10);

  useEffect(() => {
    api.messaging
      .get("ContactFieldDefinitions/list")
      .then(async (result) => {
        if (result.ok) {
          const data = await result.json();
          // Handle both old (array) and new (object) response shapes
          if (Array.isArray(data)) {
            setFields(data);
          } else {
            setFields(
              Array.isArray(data?.definitions) ? data.definitions : []
            );
            if (data?.maxCustomFields) {
              setMaxFields(data.maxCustomFields);
            }
          }
        }
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [api.messaging]);

  const removeField = useCallback(
    async (id: number) => {
      const resp = await api.messaging.delete(
        `ContactFieldDefinitions/${id}`
      );
      if (resp.ok) {
        setFields((prev) => prev.filter((f) => f.id !== id));
      }
    },
    [api.messaging]
  );

  const submit = useCallback(
    async (definition: FieldDefinition) => {
      setShowAddFieldModal(false);

      let optionsJson: string | undefined;
      if (
        definition.type === "select" &&
        Array.isArray(definition.options) &&
        definition.options.length > 0
      ) {
        optionsJson = JSON.stringify(
          definition.options.map((o) => o.value || o.label)
        );
      }

      const resp = await api.messaging.post("ContactFieldDefinitions", {
        name: definition.label.toLowerCase().replace(/\s+/g, "_"),
        label: definition.label,
        type: definition.type,
        options: optionsJson,
        sortOrder: 0,
        isRequired: false
      });

      if (resp.ok) {
        const created: ContactFieldDef = await resp.json();
        setFields((prev) => [...prev, created]);
      }
    },
    [api.messaging]
  );

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  const atLimit = maxFields > 0 && fields.length >= maxFields;

  return (
    <>
      <div className="flex h-full items-center">
        <div>
          <h2 className="text-lg font-semibold">Contact Custom Fields</h2>
          <span className="text-sm text-gray-500">
            {fields.length} / {maxFields} fields used
          </span>
        </div>
        <div className="ml-auto">
          <button
            className={`m-2 ${atLimit ? "btn-gray cursor-not-allowed opacity-50" : "btn-green"}`}
            onClick={() => !atLimit && setShowAddFieldModal(true)}
            disabled={atLimit}
            title={
              atLimit
                ? `Maximum of ${maxFields} custom fields reached. Upgrade your plan or remove unused fields.`
                : "Add a new custom field"
            }
          >
            Add Field
          </button>
        </div>
      </div>

      {/* Workflow variable hint */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-2 mb-4 text-sm text-blue-800">
        <strong>Tip:</strong> Custom fields are available as workflow variables.
        Use <code className="bg-blue-100 px-1 rounded">&lt;&lt;field_name&gt;&gt;</code> in
        Send Message, Email, or HTTP Request nodes.
        {fields.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {fields.map((f) => (
              <code
                key={f.id}
                className="bg-blue-100 px-2 py-0.5 rounded text-xs cursor-pointer hover:bg-blue-200"
                title={`Copy: <<${f.name}>>`}
                onClick={() => {
                  navigator.clipboard?.writeText(`<<${f.name}>>`);
                }}
              >
                &lt;&lt;{f.name}&gt;&gt;
              </code>
            ))}
          </div>
        )}
      </div>

      {fields.length === 0 ? (
        <div className="flex items-center justify-center mt-4 text-gray-500">
          No custom fields defined. Click "Add Field" to create one.
        </div>
      ) : (
        <table className="table-fixed w-full text-left">
          <thead>
            <tr>
              <th className="w-1/4">Label</th>
              <th className="w-1/4">Variable Name</th>
              <th className="w-1/4">Type</th>
              <th className="w-1/4"></th>
            </tr>
          </thead>
          <tbody>
            {fields.map((field) => (
              <tr key={field.id}>
                <td>{field.label}</td>
                <td>
                  <code className="text-xs bg-gray-100 px-1 rounded">
                    {field.name}
                  </code>
                </td>
                <td>{field.type}</td>
                <td>
                  <button
                    type="button"
                    className="btn-red btn-xs m-1"
                    onClick={() => removeField(field.id)}
                  >
                    <i className="fa fa-trash" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {showAddFieldModal && (
        <AddFieldModal
          show={showAddFieldModal}
          onCancel={() => setShowAddFieldModal(false)}
          submit={submit}
        />
      )}
    </>
  );
}
